import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../lib/api.js'

const TABS = [
  {
    id: 'join',
    icon: '🔗',
    label: 'Rejoindre',
    path: code => `/host/${code}`,
    available: () => true,
    visible: s => s !== 'finished',
  },
  {
    id: 'race',
    icon: '🏁',
    label: 'Course',
    path: code => `/race/${code}`,
    available: s => s === 'playing' || s === 'paused' || s === 'finished',
    visible: s => s !== 'finished',
  },
  {
    id: 'podium',
    icon: '🏆',
    label: 'Podium',
    path: code => `/podium/${code}`,
    available: s => s === 'playing' || s === 'paused' || s === 'finished',
    visible: (s, isSolo) => !isSolo,
  },
  {
    id: 'stats',
    icon: '📊',
    label: 'Stats',
    path: code => `/stats/${code}`,
    available: s => s === 'playing' || s === 'paused' || s === 'finished',
    visible: () => true,
  },
  {
    id: 'options',
    icon: '⚙️',
    label: 'Options',
    path: code => `/options/${code}`,
    available: s => s === 'playing' || s === 'paused',
    visible: s => s !== 'finished',
  },
]

/**
 * Menu de navigation latéral gauche, partagé entre les écrans hôte.
 * Sur PC : sidenav gauche avec Accueil, tabs, contrôles en bas + bulle titre flottante top-right.
 * Sur mobile : barre de tabs en bas, contrôles dans le header.
 */
export default function HostSideNav({
  code, active, statut,
  deckTitle = '',
  titleBarRight = null,
  onGameUpdate,
  startDisabled = false,
  isSolo = false,
}) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(null)
  const busy = loading !== null

  async function callAction(type) {
    setLoading(type)
    try {
      const routes = {
        start:  { url: `/api/games/${code}/start`,  method: 'PUT' },
        pause:  { url: `/api/games/${code}/pause`,  method: 'PUT' },
        resume: { url: `/api/games/${code}/resume`, method: 'PUT' },
        end:    { url: `/api/games/${code}`,         method: 'DELETE' },
      }
      const { url, method } = routes[type]
      const data = await apiFetch(url, { method })
      onGameUpdate?.(type, data)
    } catch (e) { console.error('[HostSideNav]', e.message) }
    setLoading(null)
  }

  async function handleEnd() {
    const msg = statut === 'waiting'
      ? 'Annuler cette partie ?'
      : 'Terminer cette partie ? Les joueurs seront redirigés.'
    if (!confirm(msg)) return
    callAction('end')
  }

  const showControls = ['waiting', 'playing', 'paused'].includes(statut)

  return (
    <>
      {/* Bandeau titre flottant — PC uniquement, fixed top gauche */}
      {(deckTitle || titleBarRight) && (
        <div className="hidden sm:flex fixed top-3 left-16 z-40 items-center gap-2">
          {deckTitle && (
            <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2 max-w-xs pointer-events-none">
              <span className="text-white text-sm font-medium truncate block">{deckTitle}</span>
            </div>
          )}
          {titleBarRight && (
            <div className="pointer-events-auto">
              {titleBarRight}
            </div>
          )}
        </div>
      )}

      <nav id="tour-nav" className="
        fixed bottom-0 left-0 right-0 h-14 flex flex-row z-50
        sm:static sm:h-auto sm:w-14 sm:flex-col sm:border-r sm:border-b-0
        bg-gray-900 border-t border-white/10 sm:border-t-0 shrink-0 sm:py-1
      ">
        {/* Item Accueil — PC uniquement, en tête de nav */}
        <button
          onClick={() => navigate('/home')}
          title="Accueil"
          className="hidden sm:flex flex-col items-center justify-center gap-1 py-3 px-1 w-full transition-colors text-white/55 hover:bg-white/10 hover:text-white/90 cursor-pointer"
        >
          <svg className="w-[22px] h-[22px]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          <span className="text-[9px] font-medium leading-tight text-center tracking-tight">Accueil</span>
        </button>

        {/* Séparateur après Accueil — PC uniquement */}
        <div className="hidden sm:block border-b border-white/10 mx-2 mb-1" />

        {/* Onglets de navigation */}
        {TABS.map(tab => {
          const enabled  = tab.available(statut)
          const isActive = tab.id === active
          if (!tab.visible(statut, isSolo)) return null

          return (
            <button
              key={tab.id}
              id={`tour-tab-${tab.id}`}
              title={tab.label}
              onClick={() => enabled && tab.path && !isActive && navigate(tab.path(code))}
              className={`
                flex-1 sm:flex-none flex flex-col items-center justify-center gap-0.5 sm:gap-1 py-1 sm:py-3 px-1 w-full transition-colors rounded-none
                ${isActive
                  ? 'bg-white/20 text-white'
                  : enabled
                    ? 'text-white/55 hover:bg-white/10 hover:text-white/90 cursor-pointer'
                    : 'text-white/20 cursor-not-allowed'
                }
              `}
            >
              <span className="text-xl sm:text-[22px] leading-none">{tab.icon}</span>
              <span className="text-[9px] font-medium leading-tight text-center tracking-tight">
                {tab.label}
              </span>
            </button>
          )
        })}

        {/* Boutons de contrôle — PC uniquement, en bas de nav */}
        {showControls && (
          <div id="tour-controls" className="hidden sm:flex flex-col mt-auto border-t border-white/10 pt-1">

            {/* Play — Lancer / En cours / Reprendre */}
            <button
              id="tour-btn-play"
              onClick={() => {
                if (statut === 'waiting' && !busy && !startDisabled) callAction('start')
                else if (statut === 'paused' && !busy) callAction('resume')
              }}
              title={statut === 'playing' ? 'En cours' : statut === 'paused' ? 'Reprendre la course' : 'Lancer la course'}
              className={`flex flex-col items-center justify-center gap-1 py-3 px-1 w-full transition-colors ${
                statut === 'playing'
                  ? 'bg-green-500/20 text-green-400 cursor-default pointer-events-none'
                  : busy || (statut === 'waiting' && startDisabled)
                    ? 'text-white/20 cursor-default pointer-events-none'
                    : 'text-white/40 hover:text-green-400 cursor-pointer'
              }`}
            >
              {loading === 'start' || loading === 'resume' ? (
                <span className="w-5 h-5 flex items-center justify-center text-xs">…</span>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <polygon points="5,3 19,12 5,21"/>
                </svg>
              )}
              <span className="text-[9px] font-medium leading-tight tracking-tight">
                {statut === 'playing' ? 'En cours' : statut === 'paused' ? 'Reprendre' : 'Lancer'}
              </span>
            </button>

            {/* Pause — masqué si waiting */}
            {statut !== 'waiting' && (
              <button
                id="tour-btn-pause"
                onClick={() => statut === 'playing' && !busy && callAction('pause')}
                title={statut === 'paused' ? 'Course en pause' : 'Mettre la course en pause'}
                className={`flex flex-col items-center justify-center gap-1 py-3 px-1 w-full transition-colors ${
                  statut === 'paused'
                    ? 'bg-orange-500/20 text-orange-400 cursor-default pointer-events-none'
                    : busy
                      ? 'text-white/20 cursor-default pointer-events-none'
                      : 'text-white/40 hover:text-orange-400 cursor-pointer'
                }`}
              >
                {loading === 'pause' ? (
                  <span className="w-5 h-5 flex items-center justify-center text-xs">…</span>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16"/>
                    <rect x="14" y="4" width="4" height="16"/>
                  </svg>
                )}
                <span className="text-[9px] font-medium leading-tight tracking-tight">
                  {statut === 'paused' ? 'En pause' : 'Pause'}
                </span>
              </button>
            )}

            {/* Stop / Annuler */}
            <button
              id="tour-btn-stop"
              onClick={handleEnd}
              disabled={busy}
              title={statut === 'waiting' ? 'Annuler définitivement la course' : 'Terminer définitivement la course'}
              className="flex flex-col items-center justify-center gap-1 py-3 px-1 w-full transition-colors text-white/40 hover:text-red-400 disabled:opacity-40 cursor-pointer"
            >
              {loading === 'end' ? (
                <span className="w-5 h-5 flex items-center justify-center text-xs">…</span>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12"/>
                </svg>
              )}
              <span className="text-[9px] font-medium leading-tight tracking-tight">
                {statut === 'waiting' ? 'Annuler' : 'Stop'}
              </span>
            </button>
          </div>
        )}
      </nav>
    </>
  )
}
